import { defaultsDeep, isEqual, get } from 'lodash';
export class EventRule {
    #isEditMode = false;
    #model;
    #changes;
    static DEFAULT_MODEL = {
        enabled: true,
        deleted: false,
        evaluateAt: 'pre',
        resourcelists: [],
        sites: [],
    };
    constructor(model, isEditMode = false) {
        if (!(model instanceof EventRule)) {
            defaultsDeep(model, EventRule.DEFAULT_MODEL);
        }
        this.#model = model;
        this.#changes = new Map();
        if (isEditMode) {
            this.#isEditMode = true;
        }
    }
    cloneForEdit() {
        return new EventRule(this.#model, true);
    }
    get editMode() {
        return this.#isEditMode;
    }
    get id() {
        return this.#model.id;
    }
    get org() {
        return this.#model.org;
    }
    get orgId() {
        return get(this.#model, 'org.id', this.#model.org);
    }
    get name() {
        return this.getValue('name');
    }
    set name(value) {
        this.setValue('name', value);
    }
    get description() {
        return this.getValue('description');
    }
    set description(value) {
        this.setValue('description', value);
    }
    get resourcelists() {
        return this.getValue('resourcelists');
    }
    set resourcelists(value) {
        this.setValue('resourcelists', value);
    }
    get sites() {
        return this.getValue('sites');
    }
    set sites(value) {
        this.setValue('sites', value);
    }
    get evaluateAt() {
        return this.getValue('evaluateAt');
    }
    set evaluateAt(value) {
        this.setValue('evaluateAt', value);
    }
    get rule() {
        return this.getValue('rule');
    }
    set rule(value) {
        this.setValue('rule', value);
    }
    get enabled() {
        return this.getValue('enabled') ?? true;
    }
    set enabled(value) {
        this.setValue('enabled', value);
    }
    get deleted() {
        return this.getValue('deleted') ?? false;
    }
    set deleted(value) {
        this.setValue('deleted', value);
    }
    getValue(key) {
        if (this.#changes.has(key)) {
            return this.#changes.get(key);
        }
        return this.#model[key];
    }
    setValue(key, value) {
        if (this.#isEditMode === false) {
            throw new Error('Cannot modify properties outside of edit mode.');
        }
        const currentValue = this.#model[key];
        // TODO: handle checking arrays of objects by comparing ids
        if (isEqual(currentValue, value)) {
            this.#changes.delete(key);
        }
        else {
            this.#changes.set(key, value);
        }
    }
    hasChanges() {
        return this.#changes.size > 0;
    }
    getChanges() {
        const changes = Object.fromEntries(this.#changes);
        if (this.isNew()) {
            Object.keys(this.getModel()).forEach((key) => {
                let value = this.getValue(key);
                if (key === 'org') {
                    value = get(value, 'id', value);
                }
                changes[key] = value;
            });
        }
        return changes;
    }
    resetChanges() {
        this.#changes.clear();
    }
    isNew() {
        return !this.id;
    }
    isEditMode() {
        return this.editMode;
    }
    getModel() {
        return this.#model instanceof EventRule ? this.#model.getModel() : this.#model;
    }
    updateModel(model) {
        if (this.#model instanceof EventRule) {
            this.#model.updateModel(model);
        }
        else {
            this.#model = model;
        }
    }
}
